import { filter, has, get, set } from "lodash";
import { thor } from "../../../typings/thor";
import { Enums } from "../../../thor.enums";

export class NewOnlineItemDialogController {
    public static $inject: string[] = [
        "$mdDialog",
        "$mdMedia",
        "$translate",
        "categoryService",
        "localeNumber",
        "onlinePriceService",
        "authService",
        "utilsService",
        "toaster"
    ];

    public categories: any;
    public fields: any[];
    public itemNewForm;
    public item: any;
    public saving;
    public disableCreate: boolean;
    public saleCurrencySymbol: string;

    private container: any; // bind from parent (AddItemController::createNewItemDialog)

    private saleCurrency: Enums.Currency;

    private $t;

    constructor(
        private $mdDialog: ng.material.IDialogService,
        public $mdMedia: ng.material.IMedia,
        $translate: ng.translate.ITranslateService,
        categoryService,
        public localeNumber,
        private onlinePriceService,
        private authService,
        private utilsService,
        private toaster
    ) {
        this.$t = $translate.instant;
        this.fields = categoryService.getFields();
        this.categories = categoryService.getCategories();
        this.saving = false;
    }

    public $onInit() {
        this.saleCurrency = this.container.resource.currency;
        this.saleCurrencySymbol = this.localeNumber.getCurrencySymbol(this.saleCurrency);

        this.item = {
            field: this.container.resource.field,
            category_id: this.container.resource.category_id
        };
        if (!has(this.container.resource, "options.commission_rate")) {
            const rate = get(this.authService.organization, "options.commission_rate.voluntary");
            set(this.container.resource, "options.commission_rate.voluntary", rate);
        }
    }

    public checkOrderNumberValidity() {
        const itemOrderNumber: thor.ItemMetaOrderNumber = {
            primary: get(this.item, "orderNumberPrimary"),
            secondary: get(this.item, "orderNumberSecondary")
        };
        const items: Array<thor.ItemGet> = filter(
            angular.copy(this.container.items), (item: thor.ItemGet) => item.id !== this.item.id
        );
        const sameItemFound = this.utilsService.findItemByOrderNumber(itemOrderNumber, items);

        this.itemNewForm.orderNumberPrimary.$setValidity("uniqueOrderNumber", !sameItemFound);
    }

    public checkEstimateValidity() {
        const min = this.item.estimateMin;
        const max = this.item.estimateMax;

        this.itemNewForm.estimateMin.$setValidity("estimateEmpty", !min && !max || !!min);
        this.itemNewForm.estimateMax.$setValidity("estimateEmpty", !min && !max || !!max);
        this.itemNewForm.estimateMax.$setValidity("estimateValue", !min || !max || min <= max);

        this.checkReservePriceValidity();
    }

    public checkReservePriceValidity() {
        this.itemNewForm.reservePrice.$setValidity(
            "reservePriceEstimateValue",
            !this.item.estimateMin || !this.item.reserve_price || this.item.estimateMin >= this.item.reserve_price
        );
        this.itemNewForm.reservePrice.$setValidity(
            "reservePriceStartingValue",
            !this.item.starting_price || !this.item.reserve_price || this.item.starting_price <= this.item.reserve_price
        );
    }

    public calculateFinalPrice(price: number) {
        return this.onlinePriceService.calculateFinalPrice(
            price,
            this._getCommissionRatesVoluntary(),
            this._getItemCategoryField(),
            { currency: this.saleCurrency, start_at: this.container.resource?.start_at }
        );
    }

    public getCommissionRatesLabel(price: number) {
        return this.onlinePriceService.getCommissionRatesLabel(
            price,
            this._getCommissionRatesVoluntary(),
            this._getItemCategoryField(),
            { currency: this.saleCurrency, start_at: this.container.resource?.start_at }
        );
    }

    public createItem() {
        if (this.saving || this.itemNewForm.$invalid) {
            return;
        }

        this.saving = true;

        const itemPost: thor.ItemPost = {
            category_id: this.item.category_id
        };

        if (this.item.orderNumberPrimary) {
            set(itemPost, "meta.order_number.primary", this.item.orderNumberPrimary);

            if (this.item.orderNumberSecondary) {
                set(itemPost, "meta.order_number.secondary", this.item.orderNumberSecondary);
            }
        }

        if (this.item.description) {
            itemPost.description = this.item.description.toString();
        }

        if (this.item.internal_reference) {
            itemPost.internal_reference = this.item.internal_reference;
        }

        const pricing = {
            starting_price: this.item.starting_price,
            reserve_price: this.item.reserve_price || null,
            caution: this.item.caution || null
        };

        if (this.item.estimateMin || this.item.estimateMax) {
            set(pricing, "estimates.min", this.item.estimateMin);
            set(pricing, "estimates.max", this.item.estimateMax);
        }

        itemPost.pricing = pricing;

        this.container
            .createItem(itemPost)
            .then(item => {
                this.container.insertItem(item);
                this.$mdDialog.hide();
            })
            .catch(_error => this.toaster.show(this.$t("une_erreur_est_survenue_creation_impossible"), true))
            .finally(() => (this.saving = false));
    }

    public cancel() {
        this.$mdDialog.cancel();
    }

    private _getItemCategoryField(): string {
        return get(this.item, "currentItem.category.field") || get(this.item, "field");
    }

    private _getCommissionRatesVoluntary(): any {
        return {
            voluntaryRate: get(this.container, "resource.options.commission_rate.voluntary")
        };
    }
}
